import React, { useMemo, useEffect, useState } from 'react';
import { useQuery } from 'react-apollo';
import { css, cx } from 'react-emotion';
import MaxWidth from '../Layout/MaxWidth';
import storeQuery from './StoreQuery.gql';
import { storeIdListStringToObject } from '../StoreLocator/StoreLocator';
import { Shortcodes } from '@jetshop/flight-shortcodes';
import useShopId from '../../utils/useShopId';
import Image from '@jetshop/ui/Image';
import { Link } from 'react-router-dom';
import t from '@jetshop/intl';
import { theme } from '../Theme';
import { ReactComponent as Carrot } from '../../svg/Carrot.svg';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import { useShopConfig } from '@jetshop/core/hooks/useShopConfig';
import throwErrorInDev from '@jetshop/core/helpers/throwErrorInDev';
import PageHeaderLoadingState from '../Layout/PageHeader/PageHeaderLoadingState';
import { eztradStyling } from './EztradStyling';

const containerStyling = css`
  margin-bottom: 3.5rem;
  .hero {
    height: 27.34vw;
  }
  .button-container {
    margin-top: 2.8125rem;
    a {
      padding: 1.125rem;
      text-decoration: none;
      font-weight: 500;
      color: ${theme.colors.almostBlack};
      font-size: 0.9375rem;
      border: 1px solid ${theme.colors.lightGreen};
      display: inline-flex;
      align-items: center;
      svg {
        margin-right: 0.6875rem;
        transform: rotate(90deg);
        path {
          stroke: ${theme.colors.black};
        }
      }
    }
  }
  .title-container {
    margin-top: 2.8125rem;
    h1 {
      font-weight: 700;
      font-size: 1.875rem;
      letter-spacing: 0.01em;
      color: ${theme.colors.green};
    }
  }
  .row {
    display: flex;
    margin: 1.3125rem -1.9375rem 0;
    > div {
      flex: 1;
      margin: 0 1.9375rem;
      max-width: 50%;
    }
    .description {
      margin-bottom: 3.125rem;
      line-height: 160%;
      letter-spacing: 0.01em;
      color: ${theme.colors.almostBlack};
    }
    .info {
      display: flex;
      flex-wrap: wrap;
      margin: 0 -0.5rem;
      .info-item {
        flex: 1;
        margin: 0 0.5rem 3.125rem;
        color: ${theme.colors.almostBlack};
        font-size: 1rem;
        line-height: 180%;
        h2 {
          font-weight: 500;
          font-size: 0.9375rem;
          letter-spacing: 0.05em;
          font-variant: small-caps;
          text-transform: lowercase;
          margin-bottom: 3px;
        }
      }
    }
  }
  .map-container {
    margin-top: 1.125rem;
    height: 512px;
    .map-inner {
      height: 100%;
    }
  }

  ${theme.below.md} {
    margin-bottom: 0.3125rem;
    .hero {
      height: 55vw;
    }
    .button-container {
      margin-top: 1.1875rem;
    }
    .title-container {
      margin-top: 2rem;
      h1 {
        font-size: 1.5rem;
      }
    }
    .row {
      margin: 1.3125rem 0 0;
      flex-direction: column;
      > div {
        max-width: 100%;
        margin: 0;
      }
      .description {
        margin-bottom: 2.875rem;
        font-size: 0.875rem;
        line-height: 180%;
      }
      .info {
        flex-direction: column;
        margin: 0;
        .info-item {
          margin: 0 0 2rem;
        }
      }
    }
    .map-container {
      margin-top: 0;
      height: 50vw;
    }
  }
`;

const Store = ({ category, storeIds }) => {
  const [cacheHit, setCacheHit] = useState(false);
  const isEztrad = useShopId() === 'eztrad';
  const { googleMapsApiKey } = useShopConfig();

  const storeIdList = useMemo(() => {
    return storeIdListStringToObject(storeIds);
  }, [storeIds]);

  const storeId = storeIdList.find(
    ({ categoryId }) => categoryId === category.id
  )?.storeId;

  const { data, loading, error } = useQuery(storeQuery, {
    variables: { id: storeId }
  });

  useEffect(() => {
    if (!loading) setCacheHit(true);
  }, [loading, setCacheHit]);

  if (!cacheHit) return <PageHeaderLoadingState />;
  if (error || !data.store) return 'Something went wrong';

  let {
    description,
    city,
    openHours,
    address1,
    address2,
    contact,
    coordinates
  } = data.store;
  const lng = coordinates?.longitude;
  const lat = coordinates?.latitude;

  throwErrorInDev(
    lat && lng && typeof googleMapsApiKey === 'undefined',
    'Make sure googleMapsApiKey is defined in your shop.config.js. See https://developers.google.com/maps/documentation/javascript/get-api-key'
  );

  return (
    <div className={cx(containerStyling, isEztrad && eztradStyling)}>
      {category.images && (
        <div className="hero">
          <Image fillAvailableSpace sizes={1} src={category.images} />
        </div>
      )}
      <MaxWidth>
        <div className="button-container">
          <Link to={category.parent.primaryRoute.path}>
            <Carrot />
            {t('All stores')}
          </Link>
        </div>
        <div className="title-container">
          <h1>{city}</h1>
        </div>
        <div className="row">
          {description && (
            <div
              className="description"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          )}
          <div>
            <div className="info">
              {openHours && (
                <div className="info-item">
                  <h2>{t('Opening hours')}</h2>
                  <div dangerouslySetInnerHTML={{ __html: openHours }} />
                </div>
              )}
              {(address1 || address2) && (
                <div className="info-item">
                  <h2>{t('Address')}</h2>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: `${address1 ? address1 : ''}${
                        address2 ? address2 : ''
                      }`
                    }}
                  />
                </div>
              )}
              {contact && (
                <div className="info-item">
                  <h2>{t('Contact')}</h2>
                  <div dangerouslySetInnerHTML={{ __html: contact }} />
                </div>
              )}
            </div>
          </div>
        </div>
        {lat && lng && (
          <div className="map-container">
            <LoadScript
              preventGoogleFontsLoading={true}
              googleMapsApiKey={googleMapsApiKey}
            >
              <GoogleMap
                zoom={15}
                center={{ lng, lat }}
                mapContainerClassName="map-inner"
              >
                <Marker position={{ lng, lat }} />
              </GoogleMap>
            </LoadScript>
          </div>
        )}
      </MaxWidth>
    </div>
  );
};

const StorePropsWrapper = props => {
  const { category } = props;

  const components = useMemo(() => {
    return {
      stores: props => <Store category={category} {...props} />
    };
  }, [category]);

  return (
    <Shortcodes content={category.parent.content} components={components} />
  );
};

export default StorePropsWrapper;
